import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  CircularProgress,
  alpha
} from '@mui/material';
import {
  Person as PersonIcon,
  ContactPhone as ContactPhoneIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { useLeadReport } from '../../../contexts/LeadReportContext';

const FunnelAnalysis = ({ dateRangeState, selectedTeams = [] }) => {
  const theme = useTheme();
  const { fetchStageFunnel } = useLeadReport();
  const [funnelData, setFunnelData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFunnelData = async () => {
      if (!dateRangeState) return;
      
      setLoading(true);
      const { currentStart, currentEnd } = dateRangeState;
      const startDate = currentStart.format('YYYY-MM-DD');
      const endDate = currentEnd.format('YYYY-MM-DD');

      try {
        const data = await fetchStageFunnel(startDate, endDate, selectedTeams);
        console.log('Funnel Analysis Data:', data);
        if (data && data.stages && Array.isArray(data.stages)) {
          // Map stage labels to icons and colors
          const iconMap = {
            'New Leads': PersonIcon,
            'Contacted': ContactPhoneIcon,
            'Qualified': AssignmentIcon,
            'Follow Up': AssignmentIcon,
            'Negotiation': ScheduleIcon,
            'Visit Scheduled': ScheduleIcon,
            'Visit Completed': CheckCircleIcon,
            'Closure': MoneyIcon,
            'Bookings Closed': MoneyIcon,
          };
          
          const colorMap = {
            'New Leads': '#5C8FFB',
            'Contacted': '#9B6EFB',
            'Qualified': '#EB578F',
            'Follow Up': '#EB578F',
            'Negotiation': '#F7A64B',
            'Visit Scheduled': '#F7A64B',
            'Visit Completed': '#50C88F',
            'Closure': '#4BC8A6',
            'Bookings Closed': '#4BC8A6',
          };
          
          // Transform to frontend format matching SalesFunnelOverview design
          const transformedData = data.stages.map(stage => {
            const label = stage.label || '';
            const count = stage.count || 0;
            const conversionStr = stage.conversion || '0%';
            // Parse percentage from string like "100%" or "33%"
            const percentage = parseFloat(conversionStr.replace('%', '')) || 0;
            
            return {
              label: label,
              value: count,
              percentage: percentage,
              color: colorMap[label] || '#5C8FFB',
              icon: iconMap[label] || PersonIcon,
            };
          });
          
          setFunnelData(transformedData);
        } else {
          setFunnelData([]);
        }
      } catch (error) {
        console.error('Error fetching funnel data:', error);
        setFunnelData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFunnelData();
  }, [dateRangeState, selectedTeams, fetchStageFunnel]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 2.5 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            color: 'text.primary',
            mb: 2,
            fontSize: '1.125rem'
          }}
        >
          Funnel Analysis
        </Typography>
      </Box>
      
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          background: 'background.paper',
          height: '100%',
          flex: 1,
        }}
      >
        {funnelData.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {funnelData.map((stage, index) => {
              const IconComponent = stage.icon;
              const fadedColor = alpha(stage.color, 0.15);

              return (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {/* Icon */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 32,
                      height: 32,
                    }}
                  >
                    <IconComponent
                      sx={{
                        color: stage.color,
                        fontSize: '1.5rem',
                      }}
                    />
                  </Box>

                  {/* Label and Progress Bar Container */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* Label and Values Row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: 'text.primary',
                          fontSize: '0.875rem',
                        }}
                      >
                        {stage.label}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 400,
                            color: 'text.secondary',
                            fontSize: '0.875rem',
                          }}
                        >
                          {stage.percentage}%
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            color: 'text.primary',
                            fontSize: '0.875rem',
                            minWidth: 50,
                            textAlign: 'right',
                          }}
                        >
                          {stage.value.toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Progress Bar */}
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        height: 12,
                        borderRadius: 6,
                        backgroundColor: fadedColor,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          height: '100%',
                          width: `${stage.percentage}%`,
                          backgroundColor: stage.color,
                          borderRadius: 6,
                          transition: 'width 0.5s ease-in-out',
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ p: 6, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
            <Typography variant="body2" color="text.secondary">
              No funnel data available
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default FunnelAnalysis;
